(() => {
  'use strict';

  const galleries = new Set();

  const escapeHtml = (value = '') => String(value).replace(/[&<>"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'
  })[character]);

  const imageUrl = (url, width) => {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('width', width);
    return parsed.toString();
  };

  const dimensionsForRatio = (image, ratio, size) => {
    const ratios = {
      'ratio-1x1': [1, 1], 'ratio-4x3': [4, 3], 'ratio-16x9': [16, 9],
      'ratio-21x9': [21, 9], 'ratio-3x4': [3, 4], 'ratio-9x16': [9, 16],
      'ratio-9x21': [9, 21]
    };
    const selected = ratios[ratio];
    if (selected) {
      return selected[0] >= selected[1]
        ? [size, Math.round(size * selected[1] / selected[0])]
        : [Math.round(size * selected[0] / selected[1]), size];
    }
    return [size, Math.round(size * (image.height || size) / (image.width || size))];
  };

  class VariantGallery {
    constructor(element) {
      this.element = element;
      this.configNode = element.querySelector('[data-variant-gallery-config]');
      this.main = element.querySelector('.swiper-main');
      this.thumbs = element.querySelector('.swiper-thumbs');
      if (!this.configNode || !this.main || !this.thumbs) return;

      try {
        this.config = JSON.parse(this.configNode.textContent);
      } catch (error) {
        console.error('Variant gallery configuration is invalid.', error);
        return;
      }

      // Public data requested by integrations, keyed by Shopify variant ID.
      window.variantGallery = Object.assign(window.variantGallery || {}, this.config.variants);
      this.mainWrapper = this.main.querySelector('.swiper-wrapper');
      this.thumbWrapper = this.thumbs.querySelector('.swiper-wrapper');
      this.original = {
        main: [...this.mainWrapper.children].map((slide) => slide.outerHTML),
        thumbs: [...this.thumbWrapper.children].map((slide) => slide.outerHTML)
      };
      this.zoomIcon = this.main.querySelector('.product-media-gallery-zoom')?.innerHTML || '';
      this.currentVariantId = null;
      this.renderMode = 'original';
      this.boundUpdate = (event) => this.updateFromEvent(event);
      this.element.addEventListener('click', (event) => this.openZoom(event));
      this.bind();
      this.update();
    }

    get formScope() {
      return this.element.closest('[data-quick-view-contents], .product-main') || document;
    }

    get productForm() {
      return this.formScope.querySelector('form[action*="/cart/add"]');
    }

    selectedOptions() {
      return [...this.formScope.querySelectorAll('.product-options > li')].map((option) =>
        option.querySelector('input[type="radio"]:checked, select')?.value
      ).filter((value) => value !== undefined);
    }

    selectedVariantId() {
      const form = this.productForm;
      if (!form) return '';

      // Resolve from selected options so gallery updates never depend on a
      // featured-image event or on hidden-input update timing.
      try {
        const variants = JSON.parse(form.dataset.variants || '[]');
        const options = this.selectedOptions();
        const selectedVariant = options.length
          ? variants.find((variant) => variant.options?.every((value, index) => value === options[index]))
          : variants[0];
        if (selectedVariant?.id) return String(selectedVariant.id);
      } catch (error) {
        // The hidden ID remains a safe variant-ID fallback for custom forms.
      }

      return String(form.querySelector('[name="id"]')?.value || '');
    }

    bind() {
      ['change', 'variant:change', 'product:variant-change'].forEach((name) => {
        this.formScope.addEventListener(name, this.boundUpdate);
      });
      const input = this.productForm?.querySelector('[name="id"]');
      if (input) {
        this.observer = new MutationObserver(() => this.update());
        this.observer.observe(input, { attributes: true, attributeFilter: ['value'] });
      }
      // Some themes change only the input property and emit no public variant event.
      this.poller = window.setInterval(() => this.update(), 400);
    }

    updateFromEvent(event) {
      const variant = event.detail?.variant || event.detail;
      const eventId = variant?.id || event.detail?.variantId;
      window.requestAnimationFrame(() => this.update(eventId));
    }

    update(eventId) {
      const variantId = String(eventId || this.selectedVariantId());
      if (!variantId || variantId === this.currentVariantId) return;
      this.currentVariantId = variantId;

      const featuredMediaIndex = Number(this.config.featuredMediaIndexes?.[variantId] ?? 0);
      const fallbackIndex = Number.isFinite(featuredMediaIndex) && featuredMediaIndex >= 0
        ? featuredMediaIndex
        : 0;

      // Shopify creates a hidden default variant for products without options.
      // Those products must always display their complete product.media gallery.
      if (this.config.hasOnlyDefaultVariant) {
        this.render([], fallbackIndex);
        return;
      }

      const images = (this.config.variants[variantId] || []).filter((image, index, list) =>
        image.url && list.findIndex((candidate) => candidate.url === image.url) === index
      );
      this.render(images, fallbackIndex);
    }

    openZoom(event) {
      const trigger = event.target.closest('[data-variant-gallery-zoom]');
      if (!trigger || typeof window.GLightbox !== 'function') return;
      event.preventDefault();
      const links = [...this.main.querySelectorAll('[data-variant-gallery-zoom]')];
      const lightbox = window.GLightbox({
        elements: links.map((link) => ({ href: link.href, type: 'image' })),
        effect: this.element.dataset.fullScreenEffect || 'zoom'
      });
      lightbox.openAt(links.indexOf(trigger));
    }

    mainSlide(image, index) {
      const [width, height] = dimensionsForRatio(image, this.config.imageRatio, 800);
      const zoom = this.config.fullScreen ? `<a class="product-media-gallery-zoom" href="${escapeHtml(image.url)}" data-gallery="gallery-${this.config.productId}" data-variant-gallery-zoom aria-label="${escapeHtml(this.config.fullScreenLabel)}">${this.zoomIcon}</a>` : '';
      return `<div class="swiper-slide" lazy="${index > 1}">${zoom}<img src="${escapeHtml(imageUrl(image.url, 800))}" class="img-fluid" alt="${escapeHtml(image.alt)}" width="${width}" height="${height}" loading="${index > 1 ? 'lazy' : 'eager'}"></div>`;
    }

    thumbSlide(image) {
      return `<div class="swiper-slide" data-media-type="image"><img src="${escapeHtml(imageUrl(image.url, 240))}" class="img-fluid img-thumbnail" alt="${escapeHtml(image.alt)}" width="120" height="120" loading="lazy"></div>`;
    }

    replaceSlides(swiperElement, wrapper, slides) {
      const swiper = swiperElement.swiper;
      if (swiper && !swiper.destroyed) {
        // Dynamic galleries (for example quick view) may be observed before
        // Swiper has indexed their server-rendered slides.
        swiper.recalcSlides?.();
        swiper.removeAllSlides();
        if (slides.length) swiper.appendSlide(slides);
        swiper.update();
        swiper.pagination?.render();
        swiper.pagination?.update();
        swiper.navigation?.update();
        swiper.scrollbar?.updateSize();
      } else {
        wrapper.innerHTML = slides.join('');
      }
    }

    render(images, fallbackIndex = 0) {
      const nextMode = images.length ? 'custom' : 'original';
      const shouldReplaceSlides = nextMode === 'custom' || this.renderMode !== nextMode;

      if (shouldReplaceSlides) {
        const mainMarkup = images.length
          ? images.map((image, index) => this.mainSlide(image, index))
          : this.original.main;
        const thumbMarkup = images.length
          ? images.map((image) => this.thumbSlide(image))
          : this.original.thumbs;
        this.replaceSlides(this.thumbs, this.thumbWrapper, thumbMarkup);
        this.replaceSlides(this.main, this.mainWrapper, mainMarkup);
      }

      this.renderMode = nextMode;
      const requestedIndex = nextMode === 'custom' ? 0 : fallbackIndex;
      const lastIndex = Math.max(this.mainWrapper.children.length - 1, 0);
      const activeIndex = Math.min(Math.max(requestedIndex, 0), lastIndex);
      const mainSwiper = this.main.swiper;
      if (mainSwiper && !mainSwiper.destroyed) {
        mainSwiper.slideTo(activeIndex, 0);
        mainSwiper.thumbs?.update?.(true);
      }

      const activeSlideIsVideo = Boolean(this.mainWrapper.children[activeIndex]?.querySelector('video'));
      this.main.dataset.activeSlideIsVideo = String(activeSlideIsVideo);
      this.main.closest('.swiper-main-wrapper')?.setAttribute('data-active-slide-is-video', String(activeSlideIsVideo));
      this.thumbs.hidden = this.thumbWrapper.children.length < 2;
      this.element.dataset.initialSlide = String(activeIndex);
      this.element.dispatchEvent(new CustomEvent('variant-gallery:updated', {
        bubbles: true, detail: { variantId: this.currentVariantId, images }
      }));
    }
  }

  const initialize = (root = document) => {
    root.querySelectorAll?.('product-media-gallery').forEach((element) => {
      if (element.variantGallery) return;
      element.variantGallery = new VariantGallery(element);
      galleries.add(element.variantGallery);
    });
  };

  const start = () => {
    window.requestAnimationFrame(() => initialize());
    new MutationObserver((mutations) => mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) initialize(node);
    }))).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
